import {Component, Input, OnDestroy, OnInit, inject, computed, signal} from '@angular/core';
import {PatientService} from '../../patients/services/patient.service';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {CoreService} from '../../../core/services/core.service';
import {DraftsService} from '../../../core/services/drafts.service';
import {CoreEventsService} from '../../../core/services/core-events.service';
import {PatientEventsService} from '../../patients/services/patient-events.service';
import {CommentsRepositoryService, Comment, Patient, PracticeCommentTemplate, CommentText, WebSocketService, Events, Upload} from '@xmedical/core';
import {ClinicalNoteDownloadRequestedEvent} from '@xmedical/shared';
import {FileUploaderService} from '@xmedical/uploads';
import {UserStore} from '../../../core/state';


@Component({
    selector: 'patient-comments-widget',
    templateUrl: './comments-widget.component.html',
    styleUrls: ['./comments-widget.component.scss'],
    standalone: false
})
export class CommentsWidgetComponent implements OnInit, OnDestroy {

  private patientService = inject(PatientService);
  private patientEventService = inject(PatientEventsService);
  private coreEventService = inject(CoreEventsService);
  private coreService = inject(CoreService);
  private commentsRepository = inject(CommentsRepositoryService);
  private draftService = inject(DraftsService);
  private pushService = inject(WebSocketService);
  private fileUploaderService = inject(FileUploaderService);
  private userStore = inject(UserStore);

  get hasError(): boolean {
    return this.error.length > 0;
  }

  get hasComments(): boolean {
    return this.comments.length > 0;
  }

  get patientId(): number {
    return this.patientService.currentPatient.id;
  }

  get patient(): Patient {
    return this.patientService.currentPatient;
  }

  private subscriptions: Subscription[] = [];
  private currentComment: Comment = null;
  private channelName = '';
  private readonly eventName = Events.CommentCommentsChanged;
  error = '';
  loading = true;
  comments: Comment[] = [];
  commentTemplates: PracticeCommentTemplate[] = [];
  templates: PracticeCommentTemplate[] = [];

  @Input() title = 'Comments';
  @Input() types: number[] = [1];

  // Permission computed signal
  canWrite = computed(() => this.coreService.hasPermission('Clinical.Comments.Write'));

  get shouldShowDropdown(): boolean {
    const settings = this.userStore.settings();
    const limit = settings?.commentButtonOverflowLimit;
    if (limit == null || limit <= 0) {
      return false;
    }
    return this.templates.length > limit;
  }

  private static replaceChars(string: string, find: string, replace: string): string {
    const regExp = RegExp(CommentsWidgetComponent.escapeRegExp(find), 'g');
    return string.replace(regExp, replace).trim();
  }

  private static escapeRegExp(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  }

  ngOnInit(): void {
    this.channelName = this.patientService.currentPatientEventsChannelName;
    this.commentTemplates = this.coreService.commentTemplates();
    this.subscriptions.push(this.patientEventService.currentPatient$.subscribe(() => {
      this.loadPatientComments();
    }));
    this.subscriptions.push(this.patientEventService.events$.pipe(filter(event => event.type === 'comments_updated')).subscribe(() => {
      this.loadPatientComments();
    }));
    this.subscriptions.push(this.coreEventService.events$.pipe(filter(event => event.type === 'comment_templates_loaded')).subscribe(event => {
      if (event.type === 'comment_templates_loaded') {
        this.commentTemplates = this.coreService.commentTemplates();
        this.setTemplates();
      }
    }));
    this.setTemplates();
    this.pushService.subscribe(this.channelName, this.eventName, data => {
      if (data && data.type && this.types.indexOf(data.type) > -1) {
        this.loadPatientComments();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.pushService.unsubscribe(this.channelName, this.eventName);
  }

  selectTemplate(templateId: number): void {
    const commentTemplate = this.commentTemplates.find(c => c.id === templateId);
    this.createComment(commentTemplate);
  }

  private setTemplates() {
    if (this.commentTemplates && this.commentTemplates.length > 0) {
      this.templates = this.commentTemplates.filter(t => this.types.includes(t.type?.id)).sort((a, b) => a.type.priority > b.type.priority ? 1 : -1);
    }
  }

  private loadPatientComments() {
    if (!this.patientService.hasPatientDetails) {
      return;
    }
    this.error = '';
    this.subscriptions.push(this.commentsRepository.loadPatientComments(this.patientId, this.types).subscribe({
      next: comments => {
        const sortedComments = comments.sort((a, b) => {
          return new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime();
        });
        this.comments = [...sortedComments];
        this.loading = false;
      },
      error: error => {
        this.error = 'Could not load comments for this patient';
        this.loading = false;
      }
    }));
  }

  createComment(commentTemplate: PracticeCommentTemplate) {
    this.currentComment = this.createNewComment(commentTemplate);
    this.commentsRepository.create(this.currentComment).subscribe(commentId => {
      this.currentComment = null;
      this.draftService.addNewDraft(this.patient.id, this.patient.profileId, this.patient.practiceId, 'comment', commentId, 'New Comment');
    }, error => {
      this.coreService.showErrorToast('Could not create new comment');
    });
  }

  private createNewComment(commentTemplate: PracticeCommentTemplate): Comment {
    return {
      id: null,
      typeId: commentTemplate.type.id,
      templateId: commentTemplate.id,
      commentType: commentTemplate.type,
      commentTemplate: commentTemplate,
      comment: {
        text: commentTemplate.commentTemplate.text,
        template: commentTemplate.commentTemplate
      } as CommentText,
      locked: false,
      patientId: this.patient.id,
      practiceId: this.coreService.selectedPracticeId(),
      createdDate: new Date(),
      lastModifiedDate: new Date(),
      createdUser: this.coreService.currentUser,
      lastModifiedUser: this.coreService.currentUser,
      deleted: false,
    } as Comment;
  }

  onDownloadRequested(event: ClinicalNoteDownloadRequestedEvent): void {
    const upload = event.upload;
    const getUrlFn = upload.legacy
      ? this.fileUploaderService.getSignedUrl(upload.path, 'GET', 300)
      : this.fileUploaderService.getSignedUploadsUrl(upload.path, 'GET', 300);

    getUrlFn.subscribe({
      next: (url) => {
        window.open(url, '_blank');
      },
      error: (err) => {
        this.coreService.showErrorToast('Could not download attachment');
        console.error('Failed to get download URL:', err);
      }
    });
  }
}
